import React from 'react';
import { useTranslation } from 'react-i18next';
import { TravelTimeData, TravelLeg } from '../../types';
import InfoTooltip from '../InfoTooltip/InfoTooltip';

interface TravelTimeSectionProps {
  data: TravelTimeData;
  onChange: (data: TravelTimeData) => void;
  readonly: boolean;
}

const TravelTimeSection: React.FC<TravelTimeSectionProps> = ({ data, onChange, readonly }) => {
  const { t } = useTranslation();

  const updateLeg = (leg: 'outbound' | 'inbound', field: keyof TravelLeg, value: string | boolean | number | undefined) => {
    onChange({
      ...data,
      [leg]: {
        ...data[leg],
        [field]: value,
      }
    });
  };

  const renderLeg = (leg: 'outbound' | 'inbound') => {
    const legData: TravelLeg = data[leg] || {};
    const labelKey = leg === 'outbound' ? 'travelTime.outbound' : 'travelTime.inbound';

    const ids = {
      date: `${leg}-date`,
      departure: `${leg}-departureTime`,
      arrival: `${leg}-arrivalTime`,
      break: `${leg}-breakTime`,
      km: `${leg}-km`,
    };

    return (
      <div className="travel-leg">
        <h4>{t(labelKey)}</h4>
        <div className="form-grid form-grid-travel">
          <div className="form-field">
            <label htmlFor={ids.date}>{t('travelTime.date')}</label>
            <input
              id={ids.date}
              type="date"
              value={legData.date || ''}
              onChange={(e) => updateLeg(leg, 'date', e.target.value || undefined)}
              disabled={readonly}
            />
          </div>
          <div className="form-field">
            <label htmlFor={ids.departure}>{t('travelTime.departureTime')}</label>
            <input
              id={ids.departure}
              type="time"
              value={legData.departureTime || ''}
              onChange={(e) => updateLeg(leg, 'departureTime', e.target.value || undefined)}
              disabled={readonly}
            />
          </div>
          <div className="form-field travel-arrival-field">
            <label htmlFor={ids.arrival}>
              {t('travelTime.arrivalTime')}
              <InfoTooltip text={t('travelTime.arrivalTimeTooltip')} />
            </label>
            <div className="arrival-time-row">
              <input
                id={ids.arrival}
                type="time"
                value={legData.arrivalTime || ''}
                onChange={(e) => updateLeg(leg, 'arrivalTime', e.target.value || undefined)}
                disabled={readonly}
                style={{ flex: 1 }}
              />
              <button
                type="button"
                className={`btn-next-day${legData.nextDay ? ' btn-next-day-active' : ''}`}
                onClick={() => !readonly && updateLeg(leg, 'nextDay', !legData.nextDay)}
                disabled={readonly}
                title={t('travelTime.nextDayTooltip')}
              >
                +1
              </button>
            </div>
          </div>
          <div className="form-field">
            <label htmlFor={ids.break}>{t('travelTime.breakTime')}</label>
            <input
              id={ids.break}
              type="number"
              min="0"
              value={legData.breakTime ?? 0}
              onChange={(e) => updateLeg(leg, 'breakTime', parseInt(e.target.value) || 0)}
              disabled={readonly}
            />
          </div>
          <div className="form-field">
            <label htmlFor={ids.km}>{t('travelTime.km')}</label>
            <input
              id={ids.km}
              type="number"
              min="0"
              value={legData.km ?? 0}
              onChange={(e) => updateLeg(leg, 'km', parseInt(e.target.value) || 0)}
              disabled={readonly}
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="form-section travel-time-section">
      <div className="section-title-row">
        <h3>{t('travelTime.title')}</h3>
        <InfoTooltip text={t('travelTime.sectionTooltip')} />
      </div>
      {renderLeg('outbound')}
      <hr className="travel-leg-divider" />
      {renderLeg('inbound')}
    </section>
  );
};

export default TravelTimeSection;
